import React, { useState, useMemo } from "react";
import { Search, ChevronDown, ChevronUp } from "lucide-react";
import useFAQ from "../../hooks/cs/useFAQ";
import { useInfiniteScroll } from "../../hooks/common/useInfiniteScroll";
import { useDebounce } from "../../hooks/common/useDebounce";
import LoadingSpinner from "../common/LoadingSpinner";

const FAQSection = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const { faqs, isLoading, isLoadingMore, error, hasMore, loadMore, refresh } = useFAQ();
  
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  useInfiniteScroll({
    hasMore,
    isLoading: isLoadingMore,
    loadMore,
    threshold: 200,
  });

  const filteredFAQs = useMemo(() => {
    if (!debouncedSearchQuery.trim()) return faqs;
    
    return faqs.filter(faq =>
      faq.title.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
      faq.description.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
    );
  }, [faqs, debouncedSearchQuery]);

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const formatDescription = (description: string) => {
    return description.split("\n\n").map((paragraph, index) => (
      <div key={index} className={index > 0 ? "mt-5" : ""}>
        {paragraph.split("\n").map((line, lineIndex) => (
          <span key={lineIndex}>
            {line}
            {lineIndex < paragraph.split("\n").length - 1 && <br />}
          </span>
        ))}
      </div>
    ));
  };

  if (isLoading) {
    return <LoadingSpinner message="FAQ를 불러오는 중..." size="lg" />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 text-lg">{error}</p>
        <button
          onClick={refresh}
          className="mt-4 px-6 py-2 bg-gradient-to-r from-teal-500 via-green-500 to-emerald-500 text-white rounded-lg hover:from-teal-600 hover:via-green-600 hover:to-emerald-600 transition-all duration-200"
        >
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="FAQ를 검색해보세요..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
        />
      </div>

      <div className="space-y-3">
        {filteredFAQs.map((faq) => {
          const isExpanded = expandedItems.has(faq.id);
          
          return (
            <div
              key={faq.id}
              className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-sm transition-shadow"
            >
              <button
                onClick={() => toggleExpanded(faq.id)}
                className="w-full px-6 py-4 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-base md:text-lg font-medium text-gray-900 pr-4">
                    {faq.title}
                  </h3>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  )}
                </div>
              </button>
              
              {isExpanded && (
                <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50">
                  <div className="pt-4">
                    {faq.reference_image && (
                      <div className="mb-4">
                        <img
                          src={faq.reference_image}
                          alt="FAQ 이미지"
                          className="max-w-md rounded-lg shadow-sm"
                        />
                      </div>
                    )}
                    <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {formatDescription(faq.description)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isLoadingMore && (
        <div className="flex justify-center py-8">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-teal-500 border-t-transparent"></div>
            <span className="text-gray-600">더 많은 FAQ를 불러오는 중...</span>
          </div>
        </div>
      )}

      {filteredFAQs.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">
            {debouncedSearchQuery ? `'${debouncedSearchQuery}'에 대한 검색 결과가 없습니다.` : "등록된 FAQ가 없습니다."}
          </p>
        </div>
      )}
    </div>
  );
};

export default FAQSection; 